import React, { useState } from 'react';

interface Role {
  name: string;
  description: string;
}

interface RoleCategory {
  title: string;
  roles: Role[];
}

interface RatingTableProps {
  roleCategories: RoleCategory[];
  onSubmit: (ratings: { [key: string]: number }) => void;
  column: string[];
  firstColumn: string;
}

export const RatingTable: React.FC<RatingTableProps> = ({
  roleCategories,
  onSubmit,
  column,
  firstColumn,
}) => {
  const [ratings, setRatings] = useState<{ [key: string]: number }>({});

  const columnCount = column.map((_, index) => index + 1);

  const handleRatingChange = (role: string, value: number) => {
    setRatings((prev) => ({ ...prev, [role]: value }));
  };

  const isSubmitDisabled = roleCategories.some((category) =>
    category.roles.some((role) => {
      const roleIdentifier = role.name?.trim()
        ? role.name
        : `${category?.title}-${role.description}`;
      return ratings[roleIdentifier] === undefined;
    }),
  );
  const handleSubmit = () => {
    onSubmit(ratings);
  };

  return (
    <div className="overflow-x-auto ">
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border p-2 text-purple-700">{firstColumn}</th>
            {column.map((label, index) => (
              <th key={index} className="border p-2 text-purple-700 text-center">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roleCategories.map((category) => (
            <React.Fragment key={category.title}>
              {category.title && (
                <tr className="bg-gray-50">
                  <td className="border p-2 font-bold text-xl" colSpan={7}>
                    {category.title}
                  </td>
                </tr>
              )}
              {category.roles.map((role, index) => {
                const roleIdentifier = role.name?.trim()
                  ? role.name
                  : `${category?.title}-${role.description}`;

                return (
                  <tr key={`${roleIdentifier}-${index}`} className="border">
                    <td className="border p-2 font-semibold">
                      {role.name && <span className="font-bold">{role.name}</span>}{' '}
                      {role.description}
                    </td>
                    {columnCount.map((num) => (
                      <td key={num} className="border p-2 text-center">
                        <input
                          type="radio"
                          name={`${category.title}-${roleIdentifier}`} // ✅ Ensure uniqueness
                          value={num}
                          checked={ratings[roleIdentifier] === num}
                          onChange={() => handleRatingChange(roleIdentifier, num)}
                          className="cursor-pointer w-4 h-4"
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div className="mt-4 mb-4">
        <button
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className={`px-4 py-2 rounded-md ${
            isSubmitDisabled
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          Submit
        </button>
      </div>
    </div>
  );
};

interface TopFiveResultsProps {
  ratings: { [key: string]: number };
  roleCategories: RoleCategory[];
  onRetake: () => void;
  showAllRatedRecords?: number;
}

export const TopFiveResults: React.FC<TopFiveResultsProps> = ({
  ratings,
  roleCategories,
  onRetake,
  showAllRatedRecords,
}) => {
  const sortedRoles = Object.entries(ratings)
    .sort((a, b) => b[1] - a[1]) // Sort by rating (highest first)
    .slice(0, showAllRatedRecords || 5); // Keep only top 5

  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');
      window.print();
      printArea.classList.remove('print-view');
    }
  };
  return (
    <div id="print-view">
      <h2 className="text-lg font-semibold mb-4">The top 5 roles you excel at include:</h2>
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border p-2">Role Category</th>
            <th className="border p-2">Roles</th>
            <th className="border p-2">Your Rating</th>
          </tr>
        </thead>
        <tbody>
          {sortedRoles.map(([role, rating]) => {
            const category = roleCategories.find((c) => c.roles.some((r) => r.name === role));
            return (
              <tr key={role} className="border">
                <td className="border p-2">{category?.title}</td>
                <td className="border p-2">
                  {role}: {category?.roles.find((r) => r.name === role)?.description}
                </td>
                <td className="border p-2 text-center">{rating}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 flex gap-4">
        <button
          onClick={onRetake}
          className="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-600"
        >
          Retake
        </button>
        <button
          onClick={handlePrint}
          className="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-600"
        >
          Print
        </button>
      </div>
    </div>
  );
};
